import { useEffect, useRef, useState } from 'react';
import type { Hall } from '../../api/types';
import { cityOf } from '../../lib/city';

/**
 * Derivations the home page makes from the one public venue list. Everything
 * here is read from real listings; nothing is invented when the list is short
 * or empty — callers hide their section instead.
 */

/**
 * The home page's featured venues, drawn from every city — not "near you": the
 * site never asks for the visitor's location. Within the ranking (rated venues
 * first, best rating, then most reviews, then the newest) the picks take turns
 * between cities, so one city with many listings cannot fill the whole row.
 */
export function featuredHalls(halls: Hall[], max = 7): Hall[] {
  const ranked = [...halls].sort((a, b) => {
    const ar = a.review_count > 0 ? 1 : 0;
    const br = b.review_count > 0 ? 1 : 0;
    if (ar !== br) return br - ar;
    if (ar && b.rating !== a.rating) return b.rating - a.rating;
    if (ar && b.review_count !== a.review_count) return b.review_count - a.review_count;
    return b.created_at - a.created_at;
  });
  const byCity = new Map<string, Hall[]>();
  for (const h of ranked) {
    const k = cityOf(h).toLowerCase();
    const list = byCity.get(k);
    if (list) list.push(h);
    else byCity.set(k, [h]);
  }
  const queues = [...byCity.values()];
  const out: Hall[] = [];
  while (out.length < max && queues.some((q) => q.length)) {
    for (const q of queues) {
      const h = q.shift();
      if (h && out.length < max) out.push(h);
    }
  }
  return out;
}

/** The highest-rated venue with photos; failing that, the newest with photos. */
export function spotlightHall(halls: Hall[]): Hall | null {
  const withPhotos = halls.filter((h) => h.photos.length > 0);
  if (!withPhotos.length) return null;
  const rated = withPhotos.filter((h) => h.review_count > 0);
  if (rated.length) {
    return [...rated].sort((a, b) => b.rating - a.rating || b.review_count - a.review_count)[0];
  }
  return [...withPhotos].sort((a, b) => b.created_at - a.created_at)[0];
}

export interface CityGroup {
  city: string;
  count: number;
  photo: string | null;
  /** The venue categories listed there, most common first. */
  categories: string[];
}

/** Cities with listed venues, most venues first. Case and spacing are folded. */
export function cityGroups(halls: Hall[]): CityGroup[] {
  const map = new Map<string, CityGroup & { cats: Map<string, number> }>();
  for (const h of halls) {
    const name = cityOf(h);
    if (!name) continue;
    const key = name.toLowerCase();
    let g = map.get(key);
    if (g) {
      g.count += 1;
      if (!g.photo && h.photos[0]) g.photo = h.photos[0];
    } else {
      g = { city: name, count: 1, photo: h.photos[0] ?? null, categories: [], cats: new Map() };
      map.set(key, g);
    }
    if (h.category) g.cats.set(h.category, (g.cats.get(h.category) ?? 0) + 1);
  }
  return [...map.values()]
    .map(({ cats, ...g }) => ({ ...g, categories: [...cats.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c) }))
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city));
}

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Counts from 0 to `target` the first time the element scrolls into view.
 * Shows the final value straight away for reduced motion or without
 * IntersectionObserver.
 */
export function useCountUp<T extends HTMLElement>(target: number, decimals = 0) {
  const ref = useRef<T>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!target) {
      setValue(0);
      return;
    }
    if (!el || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setValue(target);
      return;
    }
    let raf = 0;
    const run = () => {
      const start = performance.now();
      const dur = 1300;
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        const f = 10 ** decimals;
        setValue(Math.round(target * eased * f) / f);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          run();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [target, decimals]);

  return { ref, value };
}

/**
 * Runs `fn` on every animation frame in which the page scrolled or resized,
 * and once straight away. Off entirely for reduced motion unless `always`.
 */
export function useScrollFrame(fn: () => void, deps: unknown[], always = false) {
  const saved = useRef(fn);
  saved.current = fn;
  useEffect(() => {
    const reduce = prefersReducedMotion();
    let ticking = false;
    const run = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        saved.current();
      });
    };
    saved.current();
    window.addEventListener('resize', run);
    window.addEventListener('load', run);
    if (!reduce || always) window.addEventListener('scroll', run, { passive: true });
    return () => {
      window.removeEventListener('resize', run);
      window.removeEventListener('load', run);
      window.removeEventListener('scroll', run);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
